import { Routes } from '@all-hands-hq/plex-graphql-schema';
import { Button, CircularProgress, createStyles, makeStyles } from '@material-ui/core';
import React, { useState } from 'react';

import {
  GoogleCalendar,
  GoogleCalendarEvent,
  useGetPlexDetailsQuery,
  useImportGoogleCalendarEventMutation,
} from '../../../../graphql/gen/types';
import { spacings } from '../../../../theme';
import { PlexName } from '../../common/PlexName';
import { useSelectedPlexPost } from '../../details/post/useSelectedPlexPost';
import { logAnalytics } from '../../shared/logAnalytics';
import { DialogActions } from '../DialogActions';
import { DialogTopBar } from '../DialogTopBar';
import { CreateOrEditDialogTitle } from '../post/CreateOrEditDialogTitle';
import { useDialogState } from '../useDialogState';
import { ImportDialogContent } from './ImportDialogContent';

type Props = {
  plexId: string;
};

export function ImportToTimelineDialog({ plexId }: Props): React.ReactElement {
  const { data: plexData } = useGetPlexDetailsQuery({ variables: { plexId } });
  const [, setDialogState] = useDialogState();
  const [selectedCalendar, setSelectedCalendar] = useState<null | GoogleCalendar>(null);
  const [selectedEvent, setSelectedEvent] = useState<null | GoogleCalendarEvent>(null);
  const [calendarQuery, setCalendarQuery] = useState<string>('');
  const [eventQuery, setEventQuery] = useState<string>('');
  const classes = useStyles();
  // FIXME: do we need timeline here?
  const [, setSelectedPlexPost] = useSelectedPlexPost(Routes.TIMELINE_PATH);

  const [importGoogleCalendarEventMutation, { loading: importEventLoading }] = useImportGoogleCalendarEventMutation();

  const readyToImport = !importEventLoading && selectedCalendar && selectedEvent;

  const handleImport = (): void => {
    if (plexId && selectedCalendar?.id && selectedEvent?.id) {
      importGoogleCalendarEventMutation({
        variables: { event: { plexId, calendarId: selectedCalendar?.id, eventId: selectedEvent?.id } },
      }).then((result) => {
        if (result.data?.importEventFromGoogleCalendar) {
          const { id, permalink } = result.data?.importEventFromGoogleCalendar;
          logAnalytics({ name: 'post created', details: { plexId, itemId: id } });
          setDialogState(null, 'replace');
          setSelectedPlexPost({ permalink, commentsVisible: false });
        }
      });
    }
  };

  if (!plexData?.loggedIn?.plexById) {
    return <CircularProgress size={24} />;
  }

  return (
    <>
      <DialogTopBar>
        <PlexName
          name={plexData.loggedIn.plexById.name}
          color={plexData.loggedIn.plexById.color}
          className={classes.plexName}
        />
      </DialogTopBar>

      <CreateOrEditDialogTitle />

      <ImportDialogContent
        setSelectedEvent={setSelectedEvent}
        selectedEvent={selectedEvent}
        setSelectedCalendar={setSelectedCalendar}
        selectedCalendar={selectedCalendar}
        calendarQuery={calendarQuery}
        setCalendarQuery={setCalendarQuery}
        eventQuery={eventQuery}
        setEventQuery={setEventQuery}
      />

      <DialogActions
        cta={
          <Button variant="contained" disabled={!readyToImport} color="primary" onClick={handleImport}>
            Import
          </Button>
        }
      />
    </>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    plexName: {
      marginLeft: spacings.xlarge,
      marginTop: spacings.normal,
      marginBottom: spacings.normal,
    },
  })
);
